import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

type PublicHeroCopyProps = {
  eyebrow: string;
  title: string;
  accent: string;
  description: string;
  tone?: "dark" | "light";
};

export function PublicHeroCopy({
  eyebrow,
  title,
  accent,
  description,
  tone = "dark",
}: PublicHeroCopyProps) {
  const isLight = tone === "light";

  return (
    <>
      <p
        className={cn(
          "public-hero-eyebrow text-xs font-semibold uppercase tracking-[0.2em]",
          isLight
            ? "text-softiq-primary/60 dark:text-white/60"
            : "text-white/80",
        )}
      >
        {eyebrow}
      </p>
      <h1
        className={cn(
          "public-main-headline mt-6 text-[clamp(3rem,7vw,6.5rem)] font-light leading-[0.9] tracking-[-0.055em]",
          isLight ? "text-softiq-copy dark:text-white" : "text-white",
        )}
      >
        {title}
      </h1>
      <p
        className={cn(
          "mt-6 max-w-2xl text-xl font-medium leading-tight sm:text-3xl",
          isLight
            ? "text-softiq-secondary-shade-700 dark:text-softiq-secondary"
            : "text-softiq-secondary",
        )}
      >
        {accent}
      </p>
      <p
        className={cn(
          "mt-7 max-w-2xl text-base leading-7 sm:text-lg sm:leading-8",
          isLight
            ? "text-softiq-copy-muted dark:text-white/70"
            : "text-white/85",
        )}
      >
        {description}
      </p>
    </>
  );
}

export function PublicHeroChecklist({
  items,
  tone = "dark",
}: {
  items: readonly string[];
  tone?: "dark" | "light";
}) {
  return (
    <ul
      className={cn(
        "mt-9 flex flex-wrap gap-x-6 gap-y-2 text-xs",
        tone === "light"
          ? "text-softiq-copy-muted dark:text-white/60"
          : "text-white/60",
      )}
    >
      {items.map((item) => (
        <li key={item} className="inline-flex items-center gap-2">
          <Check className="size-3.5 text-softiq-secondary" aria-hidden />
          {item}
        </li>
      ))}
    </ul>
  );
}

export function PublicHeroFacts({
  facts,
}: {
  facts: ReadonlyArray<ReadonlyArray<string>>;
}) {
  return (
    <dl className="mt-10 grid border-t border-white/25 sm:grid-cols-3">
      {facts.map(([value, label]) => (
        <div
          key={`${value}-${label}`}
          className="border-b border-white/20 py-5 sm:border-b-0 sm:border-r sm:px-5 sm:first:pl-0 sm:last:border-r-0"
        >
          <dt className="text-2xl font-semibold text-white">{value}</dt>
          <dd className="mt-1 text-sm text-white/80">{label}</dd>
        </div>
      ))}
    </dl>
  );
}
